import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BounceLoader from "react-spinners/ClipLoader";
import { v4 as uuidv4 } from "uuid";
import RespondentFlowApi from "../services/respondentFlow.api";
import LoggerService from "../services/logger.service";
import useQueryParams from "../utils/query.params";
import { ERROR_ROUTE, PROFILING_ROUTE } from "../constants/routes.constants";
import { getVisiorData } from "../services/visitor.service";
import { useMediaQuery } from "@mui/material";
import { LOG_EVENTS } from "../constants/logs.constants";
import { SessionUtils } from "../utils/session.utils";

// Vendors sometimes fail to substitute their macros, leaving the literal
// placeholder (e.g. "[#transaction_id#]") in the query string.
const isUnresolvedPlaceholder = (value) =>
  typeof value === "string" && /^\[.*\]$/.test(value.trim());

const resolveOrRandom = (value) =>
  isUnresolvedPlaceholder(value) ? uuidv4() : value;

const Init = () => {
  const [paramActive, setParamActive] = useState(false);
  const [deviceType, setDeviceType] = useState(0);
  const IsMobile = useMediaQuery("(min-width:480px)");
  const IsTablet = useMediaQuery("(min-width:768px)");
  const isLaptopsAndLarger = useMediaQuery("(min-width:1200px)");
  const { surveyId, vendorId } = useParams();
  const queryParams = useQueryParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLaptopsAndLarger) setDeviceType(1);
    else if (IsTablet) setDeviceType(3);
    else setDeviceType(2);
  }, [IsTablet, isLaptopsAndLarger]);

  // Combine query + path params
  const combinedParams = {};
  queryParams.forEach((value, key) => {
    combinedParams[key] = value;
  });
  if (surveyId) combinedParams.surveyId = surveyId;
  if (vendorId) combinedParams.vendorId = vendorId;

  // Transform into expected backend schema
  const transformParams = (params) => ({
    vq_sid: params.surveyId,
    vq_vid: params.vendorId,
    vq_token: resolveOrRandom(params.transactionId ?? params.psid),
    vq_uid: resolveOrRandom(params.rid),
    is_test: params.isTest,
    ml: params.isManual,
  });

  const finalParams = transformParams(combinedParams);

  useEffect(() => {
    if (finalParams?.vq_token) {
      setParamActive(true);
    } else {
      navigate(ERROR_ROUTE);
    }
    // eslint-disable-next-line
  }, [finalParams]);

  const getStatus = async () => {
    let payload = { ...finalParams };
    payload.device_type = deviceType;
    payload.deviceType = deviceType;
    payload.fp_data = getVisiorData();
    payload.url = window.location.href;

    LoggerService.logEvent(LOG_EVENTS.JOURNEY_START, "New alt click with path params", { url: payload.url });
    LoggerService.logEvent(LOG_EVENTS.LANDING_REQUEST_SENT);

    try {
      const data = await RespondentFlowApi.post(`/v1/landing`, payload);

      if (data.token) SessionUtils.setRespondentToken(data.token);
      LoggerService.logEvent(LOG_EVENTS.LANDING_RESPONSE_RECEIVED);

      const obj = {
        paramList: data.paramList,
        sessionUid: data.sessionUid,
        surveyUid: data.surveyUid,
        vendorUid: data.vendorUid,
        token: data.token,
        tokenId: data.tokenId,
        userUid: data.userUid,
        userId: data.userId,
        traceId: data.traceId,
        vendorToken: data.vendorToken,
      };

      sessionStorage.setItem("params", JSON.stringify(obj));

      if (data.isExternalUrl === true) {
        LoggerService.logEvent(LOG_EVENTS.LANDING_REDIRECT_TO_SUPPLIER);
        LoggerService.logEvent(LOG_EVENTS.JOURNEY_END, "Redirected to supplier from init");
        window.location.href = `${data.url}`;
      } else {
        LoggerService.logEvent(LOG_EVENTS.LANDING_REDIRECT_TO_PROFILER);
        navigate(`${PROFILING_ROUTE}?token=${data.token}`);
      }
    } catch (e) {
      LoggerService.logEvent(LOG_EVENTS.LANDING_REQUEST_ERROR, "Error on alt landing: " + (e?.message || String(e)));
      navigate(ERROR_ROUTE);
    }
  };

  useEffect(() => {
    if (paramActive) getStatus();
    // eslint-disable-next-line
  }, [paramActive, deviceType]);

  return (
    <div className="flex justify-center items-center h-screen bg-gray-200">
      <div>
        <BounceLoader
          color="rgb(233, 29, 37)"
          loading={true}
          size={60}
          textAlign="center"
          aria-label="Loading Spinner"
          data-testid="loader"
        />
        <span
          style={{
            fontWeight: "700",
            marginTop: "20px",
            display: "block",
            textAlign: "center",
            color: "rgb(233, 29, 37)",
          }}
        >
          {"Loading..."}
        </span>
      </div>
    </div>
  );
};

export default Init;
